import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { AddRounded, AutoAwesomeRounded, DeleteOutlineRounded, EditRounded } from '@mui/icons-material';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, mediaBaseUrl, truncateText } from './FlowLayout';
import { fetchDestinationHierarchy } from './flowHierarchy';

const SpecialFeatureList = () => {
  const { tourTypeId } = useParams();
  const [specialFeatures, setSpecialFeatures] = useState([]);
  const [hierarchy, setHierarchy] = useState({
    destinationName: '',
    tourTypeId: '',
    tourTypeName: '',
    continentId: '',
    continentName: ''
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`https://aaryanholidays.in/backend/special-features?tour_type_id=${tourTypeId}`)
      .then((response) => setSpecialFeatures(response.data))
      .catch((error) => console.error('There was an error fetching the special features!', error));
    fetchDestinationHierarchy(tourTypeId)
      .then((response) => setHierarchy(response))
      .catch((error) => console.error('There was an error fetching the destination context!', error));
  }, [tourTypeId]);

  const filteredFeatures = useMemo(() => {
    return specialFeatures.filter((feature) =>
      String(feature.type || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [specialFeatures, search]);

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this special feature?')) {
      axios.delete(`https://aaryanholidays.in/backend/special-features/${id}`)
        .then(() => setSpecialFeatures((current) => current.filter((feature) => feature.id !== id)))
        .catch((error) => console.error('There was an error deleting the special feature!', error));
    }
  };

  return (
    <Box>
      <FlowBreadcrumbs
        items={[
          { label: 'Tour Management', to: '/manage-continents' },
          { label: hierarchy.continentName || 'Continent', to: hierarchy.continentId ? `/manage-tour-type/${hierarchy.continentId}` : undefined },
          { label: hierarchy.tourTypeName || 'Country', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined },
          { label: hierarchy.destinationName || 'Package', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined },
          { label: 'Special Features' }
        ]}
      />

      <FlowPageHeader
        title="Special Features"
        subtitle="Curate the supporting highlights attached to a package with quick search and direct edit access."
        search={search}
        searchPlaceholder="Search feature type, title, or description"
        onSearchChange={setSearch}
        action={(
          <Button
            component={Link}
            to={`/add-special-feature/${tourTypeId}`}
            variant="contained"
            startIcon={<AddRounded />}
            sx={{ borderRadius: 999, px: 2.5, py: 1.2, textTransform: 'none', fontWeight: 700 }}
          >
            Add Feature
          </Button>
        )}
      />

      <FlowStatGrid items={[{ label: 'Feature Cards', value: specialFeatures.length, icon: AutoAwesomeRounded, tone: 'linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)' }]} />

      <SoftPanel>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Thumbnail</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFeatures.map((feature) => (
                <TableRow key={feature.id} hover>
                  <TableCell>{feature.id}</TableCell>
                  <TableCell>{feature.type}</TableCell>
                  <TableCell>{feature.title}</TableCell>
                  <TableCell>{truncateText(feature.description, 110)}</TableCell>
                  <TableCell>
                    <img src={`${mediaBaseUrl}${feature.thumbnail}`} alt={feature.title} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit feature">
                      <IconButton component={Link} to={`/edit-special-feature/${feature.id}/${tourTypeId}`}>
                        <EditRounded />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete feature">
                      <IconButton onClick={() => handleDelete(feature.id)}>
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SoftPanel>
    </Box>
  );
};

export default SpecialFeatureList;
